<template>
  <HeaderBar />

  <section class="carvia-container flex flex-col justify-center">
    <div class="flex justify-between">
      <div class="flex items-center gap-3">
        <Input v-model="search" :placeholder="t('search')" variant="ghost" />
        <LegalEntityFilter />
      </div>
      <CVButton @click.prevent="router.push('create-invoice')">{{
        t('createNewInvoice')
      }}</CVButton>
    </div>

    <Tabs v-model="activeTab" :tabs="tabs" class="mb-4 mt-6" />

    <div>
      <InvoiceList :query-params="queryParams" />

      <Divider />

      <DownloadInvoiceCsv />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { FindAllInvoicesSort } from '@/entities/invoices/find-all-invoices-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import { useI18n } from 'vue-i18n';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useUiStore } from '@/stores/ui.store';
import Input from '@/components/Input.vue';
import LegalEntityFilter from '@/components/LegalEntityFilter.vue';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import { computed, ref, watch, watchEffect } from 'vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import Divider from '@/components/Divider.vue';
import DownloadInvoiceCsv from './DownloadInvoiceCsv.vue';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import router from '@/router';
import InvoiceList from './InvoiceList.vue';
import { refDebounced } from '@vueuse/core';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';

enum Tab {
  ALL = 'ALL',
  CANCELLED = 'CANCELLED',
}

const { t } = useI18n();
const uiStore = useUiStore();
watchEffect(() => {
  uiStore.setHeaderTitle(t('invoices'));
});

const activeTab = ref<Tab>(Tab.ALL);
const { legalEntityId } = useLegalEntityFilter();

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);
watch(searchQuery, () => {
  if (searchQuery.value) {
    activeTab.value = Tab.ALL;
  }
});

watch(activeTab, (newActiveTab) => {
  if (newActiveTab !== Tab.ALL) {
    search.value = '';
  }
});

const queryStatus = computed(() => {
  switch (activeTab.value) {
    case Tab.CANCELLED:
      return [
        {
          status: [InvoiceStatus.CANCELED],
        },
        {
          types: [InvoiceType.REVERSAL],
        },
      ];
    default:
      return [
        {
          status: [InvoiceStatus.OPEN, InvoiceStatus.CANCELED],
          query: searchQuery.value ? searchQuery.value : undefined,
        },
      ];
  }
});

const queryParams = computed(() => ({
  filter: queryStatus.value.map((filter) => ({
    ...filter,
    legalEntityIds: legalEntityId.value ? [legalEntityId.value] : undefined,
  })),
  sort: FindAllInvoicesSort.INVOICE_DATE,
  order: Order.DESC,
}));

const tabs = computed<TabItem[]>(() => [
  {
    title: t('all'),
    id: Tab.ALL,
  },
  {
    title: t('cancelled'),
    id: Tab.CANCELLED,
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "invoices": "Invoices",
    "search": "Search",
    "all": "All",
    "cancelled": "Cancelled",
    "createNewInvoice": "+ Create new Invoice",
    "unhandledDamages": "Unhandled damages"
  },
  "de": {
    "invoices": "Rechnungen",
    "search": "Suche",
    "all": "Alle",
    "cancelled": "Storno",
    "createNewInvoice": "+ Neue Rechnung erstellen",
    "unhandledDamages": "Unbehandelte Schäden"
  },
  "es": {
    "invoices": "Facturas",
    "search": "Buscar",
    "all": "Todas",
    "cancelled": "Anuladas",
    "createNewInvoice": "+ Crear factura nueva",
    "unhandledDamages": "Daños sin tramitar"
  }
}
</i18n>
